import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaRupeeSign, FaCalculator } from "react-icons/fa";
import {calculateSettlement, createSettlement, getLoansDetails} from "../../Api/Api";
import {useNotification} from "../Notification/NotificationContext";

const SettleLoanModal = ({ show, loanId, initialType = "DISCOUNTED_PAYMENT", onClose, onSuccess }) => {
    const [type, setType] = useState(initialType); // "CASH" or "RECOVERY"
    const [loadingCalc, setLoadingCalc] = useState(false);
    const [calculatedAmount, setCalculatedAmount] = useState(null);
    const [actualPaid, setActualPaid] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const { showNotification } = useNotification();

    useEffect(() => {
        if (show && loanId) {
            fetchCalculatedSettlementDetails();
        }
    }, [show, loanId, type]);

    const fetchCalculatedSettlementDetails = () => {
        calculateSettlement(loanId, type)
            .then((response) => setCalculatedAmount(response.data))
            .catch((error) => showNotification({ message: error, type: "error" }));
    };

    useEffect(() => {
        // when calculatedAmount updated set default actualPaid to recommended
        if (calculatedAmount && calculatedAmount.recommendedSettlement) {
            setActualPaid(calculatedAmount.recommendedSettlement);
        }
    }, [calculatedAmount]);


    const handleConfirm = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const payload = {
                type: type,
                calculatedAmount: calculatedAmount?.recommendedSettlement,
                actualPaidAmount: actualPaid ? Number(actualPaid) : calculatedAmount?.recommendedSettlement,
                notes
            };
            const res = createSettlement(loanId,payload);
            // success
            onSuccess && onSuccess(res);
            onClose && onClose();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Failed to record settlement");
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaCalculator /> Settle Loan #{loanId}
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-gray-700">Settlement Type</label>
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="mt-1 w-full p-2 border rounded-md"
                        >
                            <option value="DISCOUNTED_PAYMENT">Cash Settlement (customer pays)</option>
                            <option value="ASSET_RECOVERED">Asset Recovery (we recovered asset)</option>
                        </select>
                    </div>

                    {loadingCalc ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500" />
                            Calculating recommended settlement...
                        </div>
                    ) : calculatedAmount ? (
                        <div className="p-4 bg-gray-50 rounded-md border">
                            <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-600">Principal Outstanding</div>
                                <div className="text-lg font-semibold text-gray-800 flex items-center gap-1">
                                    <FaRupeeSign /> {Number(calculatedAmount.principalOutstanding).toLocaleString()}
                                </div>
                            </div>

                            <div className="mt-3 flex justify-between items-center">
                                <div className="text-sm text-gray-600">Recommended Settlement</div>
                                <div className="text-lg font-bold text-teal-600 flex items-center gap-1">
                                    <FaRupeeSign /> {Number(calculatedAmount.recommendedSettlement).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-sm text-gray-500">No calculation available</div>
                    )}

                    <div>
                        <label className="block text-sm text-gray-700">Actual Amount Paid (₹)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={actualPaid}
                            onChange={(e) => setActualPaid(e.target.value)}
                            className="mt-1 w-full p-2 border rounded-md"
                            placeholder="Enter actual amount paid by customer (can be different from recommendation)"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Suggestion: use recommended settlement amount shown above.
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm text-gray-700">Notes (optional)</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="mt-1 w-full p-2 border rounded-md"
                            placeholder="Add a note for this settlement (e.g. recovered asset details, agreement reference)"
                        />
                    </div>

                    {error && <div className="text-sm text-red-600">{error}</div>}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200"
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                        disabled={submitting}
                    >
                        {submitting ? "Saving..." : "Confirm Settlement"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettleLoanModal;
